import { useEffect, type RefObject } from 'react'
import { trackArticleVisualInteraction, type VisualInteractionMethod, type VisualType } from '../../../api/http/collectionService'
import type { VisualEngagementContextValue } from './VisualEngagementContext'

export function emitVisualInteraction(
  context: VisualEngagementContextValue | null,
  visualId: string,
  visualType: VisualType,
  interactionMethod: VisualInteractionMethod,
) {
  if (!context) return
  void trackArticleVisualInteraction(context.articleId, visualId, {
    visualType,
    interactionMethod,
    clientTimestamp: Date.now(),
  }).catch(() => { /* intentionally swallowed */ })
}

export function useVisualInViewTracking(
  context: VisualEngagementContextValue | null,
  ref: RefObject<HTMLElement | null>,
  visualId: string,
  visualType: VisualType,
) {
  useEffect(() => {
    const element = ref.current
    if (!element || !context?.enabled) return

    let inView = false
    const observer = new IntersectionObserver(([entry]) => {
      const nextInView = entry.isIntersecting && entry.intersectionRatio >= 0.5
      if (nextInView && !inView) {
        emitVisualInteraction(context, visualId, visualType, 'VIEW_ENTER')
      }
      if (!nextInView && inView) {
        emitVisualInteraction(context, visualId, visualType, 'VIEW_EXIT')
      }
      inView = nextInView
    }, { threshold: [0, 0.5, 1] })

    observer.observe(element)

    return () => {
      observer.disconnect()
      if (inView) {
        emitVisualInteraction(context, visualId, visualType, 'VIEW_EXIT')
      }
    }
  }, [context, ref, visualId, visualType])
}